import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { jsonFetch } from '@/lib/api/jsonFetch'
import type {
  HazmatOverride,
  Lane,
  LaneCreateResponse,
  LaneInput,
  LaneListResponse,
  Packet,
  PacketListResponse,
  Stage,
} from './types'

const PACKETS_KEY = ['export-docs', 'packets'] as const
const LANES_KEY = ['export-docs', 'lanes'] as const

export function usePackets(stage?: Stage) {
  const params = new URLSearchParams()
  if (stage) params.set('stage', stage)
  const qs = params.toString()

  return useQuery({
    queryKey: [...PACKETS_KEY, 'list', stage ?? 'all'],
    queryFn: () => jsonFetch<PacketListResponse>(`/api/export-docs/${qs ? `?${qs}` : ''}`),
    staleTime: 15_000,
  })
}

export function usePacket(packetId: number | string | null) {
  return useQuery({
    queryKey: [...PACKETS_KEY, 'detail', packetId],
    queryFn: () => jsonFetch<Packet>(`/api/export-docs/${packetId}`),
    enabled: packetId != null,
    staleTime: 5_000,
  })
}

export function useLanes() {
  return useQuery({
    queryKey: LANES_KEY,
    queryFn: () => jsonFetch<LaneListResponse>('/api/export-docs/lanes'),
    staleTime: 30_000,
  })
}

export function useRegeneratePacket() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (packetId: number) =>
      jsonFetch<Packet>(`/api/export-docs/${packetId}/regenerate`, { method: 'POST' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: PACKETS_KEY }),
  })
}

export function useHazmatOverrides() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (vars: { packetId: number; overrides: Record<string, HazmatOverride> }) =>
      jsonFetch<Packet>(`/api/export-docs/${vars.packetId}/hazmat-overrides`, {
        method: 'PATCH',
        body: { overrides: vars.overrides },
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: PACKETS_KEY }),
  })
}

export function useMarkSent() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (packetId: number) =>
      jsonFetch<Packet>(`/api/export-docs/${packetId}/mark-sent`, { method: 'POST' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: PACKETS_KEY }),
  })
}

export function useMarkApproved() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (packetId: number) =>
      jsonFetch<Packet>(`/api/export-docs/${packetId}/mark-approved`, { method: 'POST' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: PACKETS_KEY }),
  })
}

export function useMarkRejected() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (vars: { packetId: number; reason?: string }) =>
      jsonFetch<Packet>(`/api/export-docs/${vars.packetId}/mark-rejected`, {
        method: 'POST',
        body: { reason: vars.reason },
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: PACKETS_KEY }),
  })
}

export function useCancelPacket() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (vars: { packetId: number; reason?: string }) =>
      jsonFetch<Packet>(`/api/export-docs/${vars.packetId}/cancel`, {
        method: 'POST',
        body: { reason: vars.reason },
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: PACKETS_KEY }),
  })
}

export function useGenerateFinals() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (packetId: number) =>
      jsonFetch<Packet>(`/api/export-docs/${packetId}/generate-finals`, { method: 'POST' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: PACKETS_KEY }),
  })
}

// ─── Export Lane Config (Settings) ────────────────────────────────────

export function useCreateLane() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: LaneInput) =>
      jsonFetch<LaneCreateResponse>('/api/export-docs/lanes', { method: 'POST', body }),
    onSuccess: () => qc.invalidateQueries({ queryKey: LANES_KEY }),
  })
}

export function useUpdateLane() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (vars: { laneId: number; body: LaneInput }) =>
      jsonFetch<LaneCreateResponse>(`/api/export-docs/lanes/${vars.laneId}`, {
        method: 'PATCH',
        body: vars.body,
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: LANES_KEY }),
  })
}

export function useAssignCustomer() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (vars: { laneId: number; odoo_partner_id: number; partner_name_cache?: string }) =>
      jsonFetch<{ status: string }>(`/api/export-docs/lanes/${vars.laneId}/customers`, {
        method: 'POST',
        body: {
          odoo_partner_id: vars.odoo_partner_id,
          partner_name_cache: vars.partner_name_cache,
        },
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: LANES_KEY }),
  })
}

export function useUnassignCustomer() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (vars: { laneId: number; assignmentId: number }) =>
      jsonFetch<{ status: string }>(
        `/api/export-docs/lanes/${vars.laneId}/customers/${vars.assignmentId}`,
        { method: 'DELETE' },
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: LANES_KEY }),
  })
}

/** Convenience lookup — find a lane by id from an already-fetched list,
 *  used by `PartyBlockPreview` to resolve intermediary/forwarder names. */
export function findLane(lanes: Lane[] | undefined, laneId: number | null | undefined): Lane | null {
  if (!lanes || laneId == null) return null
  return lanes.find((l) => l.id === laneId) ?? null
}
